/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    getChartColorsSubset,
    chartZAxisColor
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './totalcommentingreactionsreport_styles';



/* ========== ~~~~~~~~~~ COMMENTING ANALYTICS - TOTAL COMMENTING REACTIONS REPORT ~~~~~~~~~~ ========== */
const TotalCommentingReactionsReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.commenting.totalCommentingReactions);
    const { filters } = useSelector((state) => state.app);
    const changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    const products = useMemo(() => {
        const reactions = ['upvote', 'thoughtful', 'downvote', 'love', 'funny', 'angry', 'sad', 'curious'];

        const reactionsTotalAvg = [
            { id: 'totalReactions', name: 'Total Reactions', color: '#fff' },
            { id: 'avgReactionsPerComment', name: 'Avg. Reactions per Comment', color: chartZAxisColor },
        ];

        if (overview && overview.period) {
            const reactionKeys = Object.keys(overview.period).filter(key => reactions.includes(key));
            const chartTableColors = getChartColorsSubset(reactionKeys.length)
            const commentingReactions = reactionKeys.map((key, index) => (
                { id: key, name: key[0].toUpperCase() + key.slice(1), color: chartTableColors[index] }
            ));
            return [...commentingReactions, ...reactionsTotalAvg];
        } else {
            return reactionsTotalAvg;
        }
    }
        , [overview]);
    const totalIndex = products.map(product => product.id).indexOf('totalReactions');
    const dates = details ? details[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if (status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'commenting');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);


    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        const total = products.filter(product => product.id !== 'totalReactions' && product.id !== 'avgReactionsPerComment' && (checkedOnly ? (product.checked === true) : true))
            .map(product => product.value)
            .reduce((prev, current) => prev + current, 0);
        return total;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        productArr[totalIndex] = {
            ...productArr[totalIndex],
            value: computeTableTotal(productArr)
        }

        setChartLegends(productArr);
    };

    const getBarChartKeys = () => products.map(product => product.name).reverse();

    const getBarChartColors = () => products.map(product => product.color).reverse();

    const renderBarChartLegends = () => {
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((legend, index) => {
            const productPercentage = totalValue > 0 ? ((legend.value ? legend.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';

            return (
                <ChartLegend
                    title={`${legend.name} ${chartLegends.length > 2 && legend.id !== 'totalReactions' && legend.id !== 'avgReactionsPerComment' ? productPercentageText : ''}`}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        legends[totalIndex] = {
                            ...legends[totalIndex],
                            value: computeTableTotal(legends)
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(legend.value, 'number')}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'commenting'}
                />
            )
        })
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if (checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    if (product.id === 'avgReactionsPerComment')
                        item['z'] = detailsDateData[product.id]
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if (productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        legends[totalIndex] = {
            ...legends[totalIndex],
            value: computeTableTotal(legends)
        }

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const reactionsBarChart = {
        indexBy: 'date',
        data: transformBarChartData(),
        keys: getBarChartKeys(),
        colors: getBarChartColors(),
        legends: renderBarChartLegends()
    }

    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Total Commenting Reactions'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: overview && overview.period ? formatNumber(overview.period.totalReactionsRateChange, 'percent') : 0,
                    direction: overview && overview.period ? getChangeRateDirection(overview.period.totalReactionsRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.totalReactions) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={reactionsBarChart.colors}
            keys={reactionsBarChart.keys}
            data={reactionsBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            barTotalKey={'Total Reactions'}
            zAxisId={'avgReactionsPerComment'}
            zAxisColor={chartZAxisColor}
        />
    );

    const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'commenting'}
            products={products}
            data={details ? details[selectedInterval] : null}
            csvFileName={'Total Commenting Reactions Report'}
        />
    ) : []

    const isNoData = () => {
        return (status === 'rejected') ||
            (status === 'fulfilled' && !overview?.period?.totalReactions) ||
            (status === 'fulfilled' && Object.keys(overview).length === 0);
    }

    return (
        <div name={'totalcommentingreactionsreport'} class={`${props.classes.totalcommentingreactionsreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Total Commenting Reactions'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Total Commenting Reactions</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                Count of total commenting reactions across our 8 default emojis. The Avg Reactions per
                                Comment is calculated by taking all reactions for the rolled-up period and dividing
                                that number by the total comments that received at least one reaction during that
                                same time period                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                // loading={status === 'pending' ? true : false}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={reactionsBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TotalCommentingReactionsReport;
